import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Separator,
  Switch,
} from '@redbax/ui-kit';
import type { PackagePolicy } from '@redbax/shared';

interface PackagePolicyFormProps {
  policy: PackagePolicy;
  onSave: (data: {
    name: string;
    isEnabled: boolean;
    defaultAction: string;
    minPackageAgeDays: number;
    blockCriticalSeverity: boolean;
    blockHighSeverity: boolean;
  }) => void;
  isSaving: boolean;
}

function ToggleRow({
  id,
  label,
  description,
  checked,
  onCheckedChange,
}: {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onCheckedChange: (v: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between rounded-lg border border-border-default bg-background px-4 py-3">
      <div className="space-y-0.5">
        <Label htmlFor={id} className="text-sm font-medium text-text-primary cursor-pointer">
          {label}
        </Label>
        <p className="text-xs text-text-secondary">{description}</p>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
  );
}

export function PackagePolicyForm({ policy, onSave, isSaving }: PackagePolicyFormProps) {
  const [name, setName] = React.useState(policy.name);
  const [isEnabled, setIsEnabled] = React.useState(policy.isEnabled);
  const [defaultAction, setDefaultAction] = React.useState(policy.defaultAction);
  const [minPackageAgeDays, setMinPackageAgeDays] = React.useState(policy.minPackageAgeDays);
  const [blockCriticalSeverity, setBlockCriticalSeverity] = React.useState(policy.blockCriticalSeverity);
  const [blockHighSeverity, setBlockHighSeverity] = React.useState(policy.blockHighSeverity);

  React.useEffect(() => {
    setName(policy.name);
    setIsEnabled(policy.isEnabled);
    setDefaultAction(policy.defaultAction);
    setMinPackageAgeDays(policy.minPackageAgeDays);
    setBlockCriticalSeverity(policy.blockCriticalSeverity);
    setBlockHighSeverity(policy.blockHighSeverity);
  }, [policy]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ name, isEnabled, defaultAction, minPackageAgeDays, blockCriticalSeverity, blockHighSeverity });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Policy Settings</CardTitle>
          <Switch
            id="policy-enabled-header"
            checked={isEnabled}
            onCheckedChange={setIsEnabled}
          />
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* General */}
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <Label htmlFor="policy-name">Policy Name</Label>
              <Input
                id="policy-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="default-action">Default Action</Label>
              <Select value={defaultAction} onValueChange={(v) => setDefaultAction(v as typeof defaultAction)}>
                <SelectTrigger className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Allow">Allow</SelectItem>
                  <SelectItem value="Block">Block</SelectItem>
                  <SelectItem value="RequireApproval">Require Approval</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <Separator />

          {/* Vulnerability blocking */}
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-medium text-text-primary">Vulnerability Blocking</h3>
              <p className="text-xs text-text-secondary">
                Automatically block packages with known vulnerabilities above the selected severity thresholds.
              </p>
            </div>
            <div className="space-y-2">
              <ToggleRow
                id="block-critical"
                label="Block Critical Severity"
                description="Block packages with CVSS 9.0+ vulnerabilities"
                checked={blockCriticalSeverity}
                onCheckedChange={setBlockCriticalSeverity}
              />
              <ToggleRow
                id="block-high"
                label="Block High Severity"
                description="Block packages with CVSS 7.0–8.9 vulnerabilities"
                checked={blockHighSeverity}
                onCheckedChange={setBlockHighSeverity}
              />
            </div>
          </div>

          <Separator />

          {/* Age gate */}
          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-medium text-text-primary">Package Age Gate</h3>
              <p className="text-xs text-text-secondary">
                Flag packages published more recently than the threshold. Helps guard against typosquatting and supply-chain attacks. Set to 0 to disable.
              </p>
            </div>
            <div className="max-w-xs">
              <Label htmlFor="min-age">Minimum Age (days)</Label>
              <Input
                id="min-age"
                type="number"
                min={0}
                value={minPackageAgeDays}
                onChange={(e) => setMinPackageAgeDays(parseInt(e.target.value, 10) || 0)}
                className="mt-1.5"
              />
            </div>
          </div>

          <Separator />

          <div className="flex justify-end">
            <Button type="submit" disabled={isSaving || !name.trim()}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
